// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// User Statistics
export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  bannedUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  onlineUsers: number;
  averageSessionTime: number; // in minutes
}

// Game Statistics
export enum GameTitle {
  LUDO = 'ludo',
  QUIZ = 'quiz',
  SOLITAIRE = 'solitaire'
}

export enum GameMode {
  SINGLE_PLAYER = 'single_player',
  MULTIPLAYER = 'multiplayer',
  VS_AI = 'vs_ai',
  PASS_AND_PLAY = 'pass_and_play',
  FRIENDS = 'friends'
}

export interface GameModeStats {
  mode: GameMode;
  totalPlays: number;
  uniquePlayers: number;
  averageSessionTime: number;
  completionRate: number;
}

export interface GameStatistics {
  game: GameTitle;
  totalPlays: number;
  uniquePlayers: number;
  averageSessionTime: number;
  completionRate: number;
  topScore: number;
  modes: GameModeStats[];
  playsToday: number;
  playsThisWeek: number;
  playsThisMonth: number;
}

export interface GamesPlayedOverview {
  totalGamesPlayed: number;
  totalUniquePlayers: number;
  averageGamesPerUser: number;
  gameStatistics: GameStatistics[];
  modeDistribution: {
    singlePlayer: number;
    multiplayer: number;
  };
}

// Quick Actions
export interface QuickActionStats {
  questionsAdded: number;
  notificationsSent: number;
  usersBanned: number;
  actionsToday: number;
}

export interface RecentActivity {
  id: string;
  type: 'question_added' | 'notification_sent' | 'user_banned' | 'user_registered' | 'game_completed';
  description: string;
  timestamp: string;
  userId?: string;
  username?: string;
  details?: any;
}

// Dashboard Overview
export interface DashboardOverview {
  userStatistics: UserStatistics;
  gamesPlayed: GamesPlayedOverview;
  quickActionStats: QuickActionStats;
  recentActivity: RecentActivity[];
  systemHealth: {
    serverStatus: 'healthy' | 'warning' | 'critical';
    uptime: number; // in seconds
    activeConnections: number;
    memoryUsage: number; // percentage
    cpuUsage: number; // percentage
  };
}

// Quick Action Requests
export interface AddQuestionRequest {
  question: string;
  category: string;
  difficulty: string;
  options: string[];
  correctAnswer: string;
  explanation?: string;
  points: number;
  timeLimit: number;
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  targetType: 'all' | 'specific' | 'segment';
  targetUsers?: string[]; // user IDs for specific targeting
  segment?: 'active' | 'inactive' | 'new' | 'premium'; // for segment targeting
  gameContext?: GameTitle; // for in-game notifications
  actionUrl?: string; // deep link or action
  scheduledAt?: string; // for scheduled notifications
}

export interface BanUserRequest {
  userId: string;
  reason: string;
  duration?: number; // in hours, undefined for permanent
  notifyUser: boolean;
}
